import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../components/notify/NotifyContainer";
import {
    useGetMCPSettingQuery,
    useLazyGetMCPAvailableToolsQuery,
    useUpdateMCPSettingMutation,
} from "../services/mcpApiSlice";
import type { IMCPSetting, IMCPToolConfig, IMCPToolItem } from "../types/mcpTypes";
import { MCPSettingSchema } from "../validations/mcp";

const MCP_SERVER_URL = process.env.REACT_APP_MCP_SERVER_URL || "https://langchain-mcp.ai-amaze.com";

const DEFAULT_SETTING: IMCPSetting = {
    enabled: false,
    server_url: MCP_SERVER_URL,
    max_tool_calls: 3,
    tool_configs: [],
    system_prompt_addon: "",
};

interface IProps {
    characterId: string | undefined;
}

const useMCPSettingForm = ({ characterId }: IProps) => {
    const { t } = useTranslation();
    const hasInitialLoadedRef = useRef(false);

    const { data: mcpSetting, isLoading } = useGetMCPSettingQuery(
        { characterId: characterId ?? "" },
        { skip: !characterId }
    );
    const [updateSetting] = useUpdateMCPSettingMutation();
    const [fetchTools, { isFetching: isLoadingTools }] = useLazyGetMCPAvailableToolsQuery();

    const [availableTools, setAvailableTools] = useState<IMCPToolItem[]>([]);

    const methods = useForm<IMCPSetting>({
        mode: "onChange",
        defaultValues: DEFAULT_SETTING,
        resolver: yupResolver(MCPSettingSchema()),
    });

    useEffect(() => {
        const shouldUpdate = mcpSetting !== undefined && (!methods.formState.isDirty || !hasInitialLoadedRef.current);
        if (shouldUpdate) {
            const values: IMCPSetting = {
                ...mcpSetting,
                server_url: mcpSetting.server_url || MCP_SERVER_URL,
            };
            methods.reset(values);
            hasInitialLoadedRef.current = true;
        }
    }, [mcpSetting, methods]);

    const handleLoadTools = useCallback(async () => {
        if (!characterId) return;
        try {
            const serverURL = methods.getValues("server_url") || MCP_SERVER_URL;
            const result = await fetchTools({
                characterId,
                server_url: serverURL,
            }).unwrap();
            setAvailableTools(result || []);
        } catch {
            setAvailableTools([]);
            customToast.error(t("CH.MCP_ConnectionFailed"));
        }
    }, [characterId, fetchTools, methods, t]);

    useEffect(() => {
        handleLoadTools();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const watchedToolConfigs = methods.watch("tool_configs");
    const toolConfigs = useMemo(() => watchedToolConfigs ?? [], [watchedToolConfigs]);

    const handleToggleTool = useCallback(
        (serverName: string, enabled: boolean) => {
            const currentConfigs = methods.getValues("tool_configs") ?? [];
            const existing = currentConfigs.find((tc) => tc.tool_name === serverName);

            let newConfigs: IMCPToolConfig[];
            if (existing) {
                newConfigs = currentConfigs.map((tc) => (tc.tool_name === serverName ? { ...tc, enabled } : tc));
            } else {
                newConfigs = [
                    ...currentConfigs,
                    {
                        tool_name: serverName,
                        enabled,
                        display_name: serverName,
                        timeout_seconds: 15,
                        custom_description: null,
                    },
                ];
            }
            methods.setValue("tool_configs", newConfigs, { shouldDirty: true, shouldValidate: true });
        },
        [methods]
    );

    const handleToggleSubTool = useCallback(
        (serverName: string, subToolName: string, enabled: boolean, allToolNames: string[]) => {
            const currentConfigs = methods.getValues("tool_configs") ?? [];
            const existing = currentConfigs.find((tc) => tc.tool_name === serverName);
            if (!existing) return;

            const currentEnabledTools = existing.enabled_tools ?? allToolNames;
            const newEnabledTools = enabled
                ? Array.from(new Set([...currentEnabledTools, subToolName]))
                : currentEnabledTools.filter((name) => name !== subToolName);

            const newConfigs = currentConfigs.map((tc) =>
                tc.tool_name === serverName
                    ? { ...tc, enabled_tools: newEnabledTools, enabled: newEnabledTools.length > 0 }
                    : tc
            );
            methods.setValue("tool_configs", newConfigs, { shouldDirty: true, shouldValidate: true });
        },
        [methods]
    );

    const handleUpdateToolConfig = useCallback(
        (serverName: string, field: keyof IMCPToolConfig, value: unknown) => {
            const currentConfigs = methods.getValues("tool_configs") ?? [];
            const newConfigs = currentConfigs.map((tc) =>
                tc.tool_name === serverName ? { ...tc, [field]: value } : tc
            );
            methods.setValue("tool_configs", newConfigs, { shouldDirty: true, shouldValidate: true });
        },
        [methods]
    );

    const handleFormSubmit = useCallback(
        async (data: IMCPSetting) => {
            if (!characterId) return false;
            try {
                const serverURL = data.server_url || MCP_SERVER_URL;
                await updateSetting({
                    characterId,
                    setting: { ...data, server_url: serverURL },
                }).unwrap();
                customToast.success(t("COMMON.Successfully"));
                return true;
            } catch {
                customToast.error(t("COMMON.Failed"));
                return false;
            }
        },
        [characterId, updateSetting, t]
    );

    const isToolEnabled = useCallback(
        (server: string): boolean => {
            const cfg = toolConfigs.find((tc) => tc.tool_name === server);
            return cfg?.enabled ?? false;
        },
        [toolConfigs]
    );

    const isSubToolEnabled = useCallback(
        (server: string, subToolName: string): boolean => {
            const cfg = toolConfigs.find((tc) => tc.tool_name === server);
            if (!cfg?.enabled) return false;
            if (cfg.enabled_tools == null) return true;
            return cfg.enabled_tools.includes(subToolName);
        },
        [toolConfigs]
    );

    const getToolConfig = useCallback(
        (serverName: string) => toolConfigs.find((tc) => tc.tool_name === serverName),
        [toolConfigs]
    );

    return {
        methods,
        isLoading,
        isLoadingTools,
        availableTools,
        handleLoadTools,
        handleToggleTool,
        handleToggleSubTool,
        handleUpdateToolConfig,
        handleFormSubmit,
        isToolEnabled,
        isSubToolEnabled,
        getToolConfig,
    };
};

export default useMCPSettingForm;
